import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { NudgeRecommendationDetails } from '@atlassian/convo-ai-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isBrowser } from '../../../common/utils/is-browser';

import { fetchNudgeRecommendations } from './fetchNudgeRecommendations';

type useFetchNudgeRecommendationsReturnType = {
	error?: Error;
	loading: boolean;
	recommendations: NudgeRecommendationDetails[] | undefined;
};

export const useFetchNudgeRecommendations = (
	triggerId: string,
	pageUrl: string,
	product: string,
): useFetchNudgeRecommendationsReturnType => {
	const [error, setError] = useState<Error | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [recommendations, setRecommendations] = useState<NudgeRecommendationDetails[] | undefined>(
		undefined,
	);
	const pageUrlRef = useRef<string>(pageUrl);
	// Mark the first page, as location == window.location.pathname but we still want to run makeNudgeRecommendationsRequest in this case
	const [landed, setLanded] = useState<boolean | undefined>(() => {
		return true;
	});
	const [location, setLocation] = useState<string | undefined>(
		isBrowser() ? window.location.pathname : undefined,
	);

	const makeNudgeRecommendationsRequest = useCallback(async () => {
		if (!fg('aix_nudge_recommendation_migration_enabled')) {
			setLoading(false);
			return;
		}

		// TODO: remove temporary hack when Confluence blog 500 bug is fixed
		const isBlog = pageUrl.includes('/blog/');
		if (isBlog) {
			setLoading(false);
			return;
		}

		if (!product) {
			setLoading(false);
			return;
		}

		try {
			const response = await fetchNudgeRecommendations({ triggerId, pageUrl, product });
			setRecommendations(
				response?.triggers.flatMap((trigger) =>
					trigger.recommendations.map((recommendation) => recommendation.recommendation_details),
				),
			);
			setError(undefined);
		} catch (error: unknown) {
			setRecommendations(undefined);
			setError(error as Error);
		} finally {
			setLoading(false);
		}
	}, [triggerId, pageUrl, product]);

	useEffect(() => {
		// Prevents makeNudgeRecommendationsRequest duplicate calls on the same pageUrl
		if (landed || location !== window.location.pathname) {
			setLocation(window.location.pathname);
			setLanded(false);
			void makeNudgeRecommendationsRequest();
		}
	}, [makeNudgeRecommendationsRequest, location, landed]);

	// Reset state immediately when pageUrl changes to prevent stale recommendations
	useEffect(() => {
		if (pageUrlRef.current !== pageUrl) {
			pageUrlRef.current = pageUrl;
			setRecommendations(undefined);
			setError(undefined);
			setLoading(true);
		}
	}, [pageUrl]);

	return useMemo(
		() => ({
			recommendations: loading ? undefined : recommendations,
			loading,
			error,
		}),
		[loading, error, recommendations],
	);
};
